import { createTheme, PaletteOptions } from '@mui/material';

// Coates branding colours
const coatesColours = {
  blue: '#00A2DE',
  green: '#75bb43',
  red: '#EE3135',
  yellow: '#FEC325',
  black: '#000000',
  white: '#FFF',
  charcoal: '#485156',
};

export const colorPickerColors = {
  yellow: '#FFBC0D',
  red: '#DB0007',
  black: '#000000',
  darkGray: '#2D2D2D',
  lightGray: '#F7F7F7',
};

const switchboardColours = {
  primary: {
    main: '#025AC5',
    light: '#D1ECF8',
    dark: '#004397',
  },
  error: {
    main: '#BA0000',
    dark: '#99001C',
    light: '#FFBEC2',
  },
  warning: {
    main: '#FEC325',
    dark: '#974000',
    light: '#FFE397',
  },
  info: {
    main: '#00A2DE',
    dark: '#004C6B',
    light: '#C6E7FF',
  },
  success: {
    main: '#036103',
    dark: '#155724',
    light: '#75BB43',
  },
};

const customPalette: PaletteOptions = {
  brand: {
    blue: '#00A2DE',
    lightBlue: '#EEF5FF',
    skyBlue: '#D8E2FF',
    green: '#75BB43',
    lightGreen: '#F4FAF0',
    ash: '#EAEAEA',
    mediumGrey: '#D7D7D7',
    red: '#EE3035',
    yellow: '#FEC325',
    black: '#000000',
    charcoal: '#4E5156',
    nero: '#1a1a1a',
    whitesmoke: '#F5F5F5',
    disable: '#C2C2C2',
    lightblack: '#444444',
    antiFlash: '#F1F1F1',
    pomonaGreen: '#155724',
    parsley: '#155724',
    brown: '#856404',
    persianPlum: '#721C24',
    yellowGreen: '#A0DD74',
    darkBlue: '#004085',
    disabled: '#EFEFEF',
    darkGrey: '#9D9D9D',
    lightGrey: '#F6F6F6',
    warning: '#FF9800',
    grey: '#292929',
    borderGrey: '#DDDDDD',
    screenLightBlue: '#D8E2FF',
    error: '#D32F2F',
    transparentBlack: '#0000008f',
    muted: '#00000099',
    cardSurface: '#F2F3F9',
  },
  mode: 'light',
  common: {
    black: coatesColours.black,
    white: coatesColours.white,
  },
  primary: {
    main: switchboardColours.primary.main,
    light: switchboardColours.primary.light,
    dark: switchboardColours.primary.dark,
    lighter: '#EEF5FF',
    lightest: '#FFFFFF',
    contrastText: coatesColours.white,
  },
  secondary: {
    dark: '#000000',
    main: '#4E5156',
    light: '#898C90',
    lighter: '#EDEDED',
    lightest: '#F4F3F5',
    contrastText: '#FFFFFF',
  },
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
    A100: '#F5F5F5',
    A200: '#EEEEEE',
    A400: '#BDBDBD',
    A700: '#616161',
  },
  success: {
    main: switchboardColours.success.main,
    dark: switchboardColours.success.dark,
    light: switchboardColours.success.light,
  },
  divider: 'rgba(0, 0, 0, 0.12)',
};

const customTypography = {
  fontFamily: ['Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(','),
  h1: {
    fontSize: '2.5rem',
    fontWeight: 400,
  },
  h2: {
    fontSize: '2.125rem',
    fontWeight: 400,
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 400,
  },
  h4: {
    fontSize: '1.375rem',
    fontWeight: 400,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 400,
  },
  h6: {
    fontSize: '1.125rem',
    fontWeight: 400,
  },
  body1: {
    fontWeight: 300,
  },
  allVariants: {
    color: coatesColours.black,
  },
};

const customComponents = {
  MuiInputBase: {
    styleOverrides: {
      input: {
        fontWeight: 300,
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: switchboardColours.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: switchboardColours.primary.main,
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: switchboardColours.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: switchboardColours.primary.main,
        },
      },
    },
  },
  MuiButton: {
    defaultProps: {
      variant: 'contained' as 'contained',
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
      },
    },
  },
  MuiAccordion: {
    defaultProps: {
      disableGutters: true,
    },
    styleOverrides: {
      root: {
        position: 'unset',
        boxShadow: 'none',
        marginBottom: '30px',
        '&::before': {
          display: 'none',
        },
      },
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        padding: '8px 0 0',
      },
    },
  },
  MuiChip: {
    defaultProps: {
      variant: 'filled' as 'filled',
    },
  },
};

const customStatus = {
  success: {
    dark: '#155724',
    light: '#D4EDDA',
  },
  danger: {
    dark: '#721C24',
    light: '#F8D7DA',
  },
  warning: {
    dark: '#FEC325',
    light: '#FFF3CD',
  },
  info: {
    dark: '#898C90',
    light: '#FFFFFF',
  },
  scheduled: {
    dark: '#004397',
    light: '#D1ECF8',
  },
  live: {
    dark: '#036103',
    light: '#C7F2A9',
  },
  expired: {
    dark: '#99001C',
    light: '#FFBEC2',
  },
};

const customTooltip = {
  secondary: {
    info: switchboardColours.info.light,
  },
  primary: {
    info: 'rgba(97, 97, 97, 0.9)',
  },
};

export const theme = createTheme({
  typography: customTypography,
  components: customComponents,
  palette: customPalette,
  status: customStatus,
  tooltip: customTooltip,
});

export const ValidationTextFieldColor = (status: string | undefined) => {
  switch (status) {
    case 'active':
      return theme.palette.primary.main;
    case 'error':
      return theme.palette.brand.red;
    case 'disabled':
      return theme.palette.primary.lighter;
    default:
      return customPalette.brand.disable;
  }
};

const ITEM_HEIGHT = 26;
const ITEM_PADDING_TOP = 8;

export const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 150,
    },
  },
};
